"use client";

import { useQuery } from "@apollo/client";
import { GET_BENEFITS, IBenefitsResponse } from "gql/Benefits/queries";
import { ArrowRight, Calendar, Gift, Users } from "lucide-react";
import useTranslation from "next-translate/useTranslation";
import { ExploreSideCardHeaderV2 } from "components/ui/v2/explore/ExploreSideCardHeaderV2";
import Link from "next/link";
import { PRIVATE_ROUTES } from "routes/routes";
import { useEffect, useMemo, useState } from "react";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger
} from "shadcn/components/ui/accordion";
import { LiquidPill } from "components/ui/v2";
import { Card as CardV2 } from "components/ui/v2/card";
import { IBenefit } from "types/Benefit.type";
import {
  isCacheValid,
  useExploreWidgetsCacheRefresh,
  useExploreWidgetsStore
} from "stores/explore/useExploreWidgetsStore";

interface BenefitsWidgetProps {
  readonly variant?: "default" | "v2";
}

export default function BenefitsWidget(props: BenefitsWidgetProps) {
  const { variant = "default" } = props;
  const { t } = useTranslation("common");
  const [allBenefits, setAllBenefits] = useState<IBenefit[]>([]);
  const { benefits, setBenefits, hasHydrated } = useExploreWidgetsStore();
  useExploreWidgetsCacheRefresh();
  const hasValidCache = isCacheValid(benefits.lastFetched);

  const { loading } = useQuery<IBenefitsResponse>(GET_BENEFITS, {
    fetchPolicy: "network-only",
    skip: !hasHydrated || hasValidCache,
    onCompleted: (data) => {
      if (data?.benefits) {
        setBenefits(data.benefits);
      }
    }
  });

  useEffect(() => {
    if (benefits.data) {
      setAllBenefits(benefits.data);
    }
  }, [benefits.data]);

  // Filtrar beneficios activos
  // Mostrar beneficios con fecha de caducidad, límite de capacidad, o sin fecha (duran para siempre)
  const filteredBenefits = useMemo(() => {
    return allBenefits.filter((benefit) => benefit.isActive);
  }, [allBenefits]);

  if (loading && !benefits.data) {
    return (
      <CardV2
        size="full"
        variant="default"
        tone="default"
        padding="none"
        shadow="subtle"
        className="w-full max-w-full"
      >
        <div className="px-4 py-3" role="status" aria-label="Loading benefits">
          <div className="mb-4 flex items-center gap-2">
            <div className="relative h-4 w-4 overflow-hidden rounded bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
            <div className="relative h-3 w-36 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
          </div>
          <div className="space-y-3">
            <div className="relative h-3 w-full overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
            <div className="relative h-3 w-5/6 overflow-hidden rounded-full bg-gray-200 dark:bg-gray-700">
              <div className="absolute inset-0 -translate-x-full animate-shimmer bg-gradient-to-r from-transparent via-white/60 to-transparent dark:via-white/10" />
            </div>
          </div>
        </div>
      </CardV2>
    );
  }

  // Si no hay beneficios que mostrar, no renderizar el componente
  if (filteredBenefits.length === 0) {
    return null;
  }

  return (
    <CardV2
      size="full"
      variant="default"
      tone="default"
      padding="none"
      shadow="subtle"
      className="w-full max-w-full"
    >
      <Accordion type="single" collapsible={true} defaultValue="item-1">
        <AccordionItem value="item-1" className="rounded-xl border-b-0">
          <AccordionTrigger className="px-4 py-1.5">
            <ExploreSideCardHeaderV2
              title={t("Beneficios destacados")}
              icon={
                variant === "v2" ? (
                  <Gift size={14} strokeWidth={1.1} color="#0073FF" />
                ) : null
              }
              className="w-full pr-1"
            />
          </AccordionTrigger>
          {filteredBenefits?.map((benefit, index) => (
            <Link
              href={PRIVATE_ROUTES.benefitsPortal.path}
              key={benefit.id}
              className=""
            >
              <AccordionContent
                className={`flex w-full place-items-start items-start gap-2 border-b-[1px] bg-white px-4 py-2 align-middle transition-all hover:bg-accent dark:border-gray-600 dark:bg-black ${
                  index === filteredBenefits.length - 1
                    ? "border-b-0"
                    : ""
                }`}
              >
                {/* Icono del beneficio */}
                <div className="mt-0.5 flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-lg bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20">
                  <Gift
                    size={12}
                    className="text-blue-600 dark:text-blue-400"
                  />
                </div>

                <div className="flex flex-1 flex-col justify-start gap-0.5">
                  {/* Nombre del beneficio */}
                  <span className="line-clamp-1 text-ellipsis text-[12px] font-semibold leading-tight text-gray-900 dark:text-gray-100">
                    {benefit.name}
                  </span>

                  {/* Descripción del beneficio */}
                  {benefit.description && (
                    <span className="line-clamp-1 text-ellipsis text-[10px] leading-snug text-slate-600 dark:text-zinc-400">
                      {benefit.description}
                    </span>
                  )}

                  {/* Información adicional */}
                  <div className="mt-0.5 flex flex-wrap gap-1">
                    {benefit.hasCapacityLimit && benefit.capacityLimit && (
                      <LiquidPill
                        icon={<Users size={14} />}
                        text={t("Cupos limitados")}
                        variant="gold"
                        size="xxs"
                      />
                    )}
                    {benefit.endDate && (
                      <LiquidPill
                        icon={<Calendar size={14} />}
                        text={`${t("Hasta")} ${new Date(benefit.endDate).toLocaleDateString()}`}
                        variant="sky"
                        size="xxs"
                      />
                    )}
                    {!benefit.endDate && (
                      <LiquidPill
                        icon={<Calendar size={14} />}
                        text={t("Sin caducidad")}
                        variant="emerald"
                        size="xxs"
                      />
                    )}
                  </div>
                </div>
              </AccordionContent>
            </Link>
          ))}
          <Link
            href={PRIVATE_ROUTES.benefitsPortal.path}
            className="flex items-center justify-center gap-1.5 rounded-b-xl border-t-[1px] bg-white px-4 py-2.5 text-[11px] font-semibold text-blue-600 transition-all hover:bg-accent dark:border-gray-600 dark:bg-black dark:text-blue-400"
          >
            {t("Ver portal de beneficios")}
            <ArrowRight size={12} />
          </Link>
        </AccordionItem>
      </Accordion>
    </CardV2>
  );
}
